import { defineQuery } from "@rocicorp/zero";
import type { ExpressionBuilder } from "@rocicorp/zero";
import { z } from "zod";
import type {} from "../zero-context";
import { zql, type Schema } from "../schema";
import { FULL_MEMBER_ROLES } from "../constants";
import { channelMemberGuard } from "./access-filters";

/**
 * Filter that allows access to user_presence rows belonging to users the
 * caller can see. Mirrors `userAccessFilter` in queries/users.ts:
 *   1. Self
 *   2. Full workspace members (caller sees presence of all their workspace's users)
 *   3. Channel peers (collaborators see presence of users in shared channels/DMs)
 *
 * Same asymmetric soft-delete semantics as users — the caller's
 * membership must be active (`removed_at IS NULL`); the target user's
 * membership can be soft-deleted (kicked users stay resolvable for
 * history rendering).
 */
function userPresenceAccessFilter(sub: string) {
  return (eb: ExpressionBuilder<"user_presence", Schema>) =>
    eb.or(
      eb.cmp("user_id", sub),
      eb.exists("user", (uq) =>
        uq.whereExists("workspace_memberships", (q) =>
          q.whereExists("workspace", (wq) =>
            wq.whereExists("members", (mq) =>
              mq
                .where("user_id", sub)
                .where("role", "IN", FULL_MEMBER_ROLES)
                .where("removed_at", "IS", null),
            ),
          ),
        ),
      ),
      eb.exists("user", (uq) =>
        uq.whereExists("channel_memberships", (q) =>
          q.whereExists("channel", channelMemberGuard(sub)),
        ),
      ),
    );
}

export const userPresenceQueries = {
  /**
   * All presence rows visible to the caller — analogous to `users.all`.
   * `useUserPresenceMap` consumes this and projects into a Map keyed by
   * user_id. Workspace-scoped via the access filter; not a full-table read.
   */
  all: defineQuery(({ ctx: { sub } }) =>
    zql.user_presence.where(userPresenceAccessFilter(sub)),
  ),
  byUserId: defineQuery(z.string(), ({ args: id, ctx: { sub } }) =>
    zql.user_presence
      .where("user_id", id)
      .where(userPresenceAccessFilter(sub))
      .one(),
  ),
  byWorkspace: defineQuery(z.string(), ({ args: workspaceId, ctx: { sub } }) =>
    zql.user_presence
      .where(userPresenceAccessFilter(sub))
      .whereExists("user", (uq) =>
        uq.whereExists("workspace_memberships", (q) =>
          q.where("workspace_id", workspaceId),
        ),
      ),
  ),
  /**
   * Users currently typing in a given channel. Replacement for
   * `users.typing(channelId)` once readers migrate (PR 1.5.3). Joins
   * `user` via .related so the typing-indicator UI gets `display_name`
   * inline. is_coworker exclusion is enforced via the inner whereExists
   * — internal coworkers stream via SSE, external coworkers shouldn't
   * surface in the typing indicator either.
   *
   * Joining users via .related is safe for this query specifically
   * because users (post-Phase-1.5) is identity-only and rarely
   * mutated; the typing-IVM trap only fires for joins onto
   * frequently-mutated tables.
   */
  typing: defineQuery(z.string(), ({ args: channelId, ctx: { sub } }) =>
    zql.user_presence
      .where("typing_in", channelId)
      // Auth: typing user is a non-coworker AND a non-removed member of
      // this channel, AND the viewer is also a non-removed member of the
      // same channel. Single indexable EXISTS chain — replaces
      // userPresenceAccessFilter (3 OR'd EXISTS) for this query
      // specifically. Per April 22 perf investigation: the OR forced
      // SQLite into per-keystroke full-table scans of users +
      // user_presence. The other queries on this file (all, byUserId,
      // byWorkspace, online) keep the broader filter
      // — they legitimately need it.
      .whereExists("user", (uq) =>
        uq
          .where("is_coworker", false)
          .whereExists("channel_memberships", (q) =>
            q
              .where("channel_id", channelId)
              .where("removed_at", "IS", null)
              .whereExists("channel", channelMemberGuard(sub)),
          ),
      )
      .related("user"),
  ),
  online: defineQuery(({ ctx: { sub } }) =>
    zql.user_presence
      .where("is_online", true)
      .whereExists("user", (uq) => uq.where("is_deactivated", false))
      .where(userPresenceAccessFilter(sub)),
  ),
};
